import Swal, { type SweetAlertOptions, type SweetAlertResult } from "sweetalert2";
import { colors } from "../theme/tokens";

/**
 * SweetAlert2 helpers.
 *
 * The dialog itself (shape, type, default brand-coloured confirm, neutral
 * Cancel) is themed once in index.css, so a plain `Swal.fire({...})` already
 * looks right — don't pass `confirmButtonColor` for ordinary confirms.
 *
 * The only colour a call site should ever choose is "this confirm destroys
 * something". Use `confirmDanger` for that rather than a hex value.
 */

/** Token colours for the rare call that must set a button colour itself. */
export const swalColors = {
  confirm: colors.primary[700],
  danger: colors.danger[600],
} as const;

/**
 * Asks before a destructive action. Red confirm, neutral Cancel, warning icon.
 *
 *   const { isConfirmed } = await confirmDanger({
 *     title: "Delete this asset?",
 *     text: "This can't be undone.",
 *     confirmButtonText: "Delete",
 *   });
 */
export function confirmDanger(
  options: SweetAlertOptions
): Promise<SweetAlertResult> {
  return Swal.fire({
    icon: "warning",
    showCancelButton: true,
    cancelButtonText: "Cancel",
    ...options,
    confirmButtonColor: swalColors.danger,
  } as SweetAlertOptions);
}

/**
 * Asks before a non-destructive action that still deserves a pause
 * (disconnecting an account, sending to many recipients). Brand confirm.
 */
export function confirmAction(
  options: SweetAlertOptions
): Promise<SweetAlertResult> {
  return Swal.fire({
    icon: "question",
    showCancelButton: true,
    cancelButtonText: "Cancel",
    ...options,
  } as SweetAlertOptions);
}
